import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useEntityContext } from '@/hooks/entity-context.hook';
import { Button } from '@/components/ui/button';
import RoleBadge from '@/components/ui/role-badge';
import { Spinner } from '@/components/spinner';
import { Skeleton } from '@/components/ui/skeleton';
import NezenxLogo from '@/assets/svg/logo.svg?raw';

export default function EntitySelectionScreen() {
  const {
    entities,
    initialized,
    loading,
    switching,
    error,
    initialize,
    switchEntity,
  } = useEntityContext();
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);

  useEffect(() => {
    if (!initialized && !loading) void initialize();
  }, [initialize, initialized, loading]);

  const handleSelect = async (entityId: string) => {
    setSelectedEntityId(entityId);
    const selected = await switchEntity(entityId);
    if (selected) window.location.assign('/dashboard');
    else setSelectedEntityId(null);
  };

  return (
    <main className="flex min-h-screen items-start justify-center bg-secondary px-[1.6rem] py-[2.4rem] sm:items-center sm:px-[2.4rem] sm:py-[4rem]">
      <div className="w-full max-w-[56rem]">
        <span
          aria-label="NezenX"
          className="mb-[2rem] block w-fit text-[#1D1D1D] dark:text-white [&>svg]:h-auto [&>svg]:w-[13rem] sm:[&>svg]:w-[15rem]"
          dangerouslySetInnerHTML={{ __html: NezenxLogo }}
        />

        <section className="w-full rounded-[1.6rem] border border-primary bg-main px-[1.6rem] pb-[1.6rem] pt-[1.3rem] shadow-sm sm:rounded-[2rem] sm:px-[3.2rem] sm:pb-[3.2rem] sm:pt-[2.4rem]">
          <div className="mb-[2rem] sm:mb-[2.4rem]">
          <h1 className="text-[2rem] font-semibold text-main sm:text-[2.2rem]">Choose a business</h1>
          <p className="mt-[0.5rem] text-[1.2rem] leading-[1.7rem] text-second sm:text-[1.3rem] sm:leading-[1.8rem]">
            Select the business workspace you want to open. You can change it later from the sidebar.
          </p>
          </div>

          <div className="space-y-[1rem]">
            {!initialized || loading
              ? Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-[7.2rem] w-full rounded-[1.2rem]" />
              ))
              : entities.map((entity) => (
                <Button
                  key={entity.id}
                  type="button"
                  variant="outline"
                  disabled={switching}
                  onClick={() => handleSelect(entity.id)}
                  className="h-auto min-h-[7.2rem] w-full rounded-[1.2rem] border-primary bg-secondary px-[1.3rem] py-[1.2rem] text-left hover:border-[#57E8BA] hover:bg-[#57E8BA]/5 disabled:opacity-60 sm:min-h-[7.6rem] sm:px-[1.6rem] [&>span]:w-full"
                >
                  <span className="flex min-w-0 flex-1 items-center">
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-[0.7rem]">
                        <span className="truncate text-[1.4rem] font-semibold text-main sm:text-[1.5rem]">
                          {entity.name}
                        </span>
                        <RoleBadge
                          role={entity.role}
                          className="h-[2rem] gap-[0.45rem] px-[0.6rem] text-[1.05rem] [&>span]:h-[0.55rem] [&>span]:w-[0.55rem]"
                        />
                      </span>
                      <span className="mt-[0.35rem] block truncate text-[1.1rem] font-normal text-second sm:text-[1.2rem]">
                        {entity.code || 'Business workspace'}
                      </span>
                    </span>
                  </span>
                  <span className="ml-[0.8rem] grid h-[3.2rem] w-[3.2rem] shrink-0 place-items-center rounded-[0.8rem] text-second">
                    {switching && selectedEntityId === entity.id
                      ? <Spinner width={16} height={16} />
                      : <ArrowRight className="h-[1.7rem] w-[1.7rem]" />}
                  </span>
                </Button>
              ))}
          </div>

          {error && <p className="mt-[1.4rem] rounded-[0.8rem] border border-red-500/30 bg-red-500/10 px-[1.2rem] py-[1rem] text-[1.2rem] text-red-500 sm:text-[1.3rem]">{error}</p>}
        </section>
      </div>
    </main>
  );
}
